import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { useAuthStore } from '@/stores/auth.store';
import { useWorkspaceStore } from '@/stores/workspace.store';

const routes: RouteRecordRaw[] = [
  {
    path: '/auth',
    component: () => import('@/views/auth/AuthLayout.vue'),
    meta: { public: true },
    children: [
      { path: 'login', name: 'login', component: () => import('@/views/auth/LoginView.vue') },
      { path: 'register', name: 'register', component: () => import('@/views/auth/RegisterView.vue') },
      { path: 'forgot', name: 'forgot-password', component: () => import('@/views/auth/ForgotPasswordView.vue') },
      { path: 'reset', name: 'reset-password', component: () => import('@/views/auth/ResetPasswordView.vue') },
      { path: 'verify', name: 'verify-email', component: () => import('@/views/auth/VerifyEmailView.vue') },
      { path: 'invitation', name: 'invitation', component: () => import('@/views/auth/InvitationView.vue') },
    ],
  },
  {
    path: '/',
    component: () => import('@/components/layout/AppShell.vue'),
    children: [
      { path: '', name: 'dashboard', component: () => import('@/views/DashboardView.vue') },
      // Contributed by the Dashboard Extended plugin. Company-scoped, like the
      // dashboard it edits; the view says so when the plugin is off, and the
      // entry point is a button on the dashboard rather than a nav item, since
      // it exists to change the screen you are already looking at.
      { path: 'dashboard/editor', name: 'dashboard-editor', component: () => import('@/views/dashboard/DashboardEditorView.vue') },
      { path: 'search', name: 'search', component: () => import('@/views/SearchView.vue') },
      { path: 'notifications', name: 'notifications', component: () => import('@/views/NotificationsView.vue') },
      { path: 'inbox', name: 'inbox', component: () => import('@/views/InboxView.vue') },
      // Contributed by the Chat plugin. Company-scoped: it lists every chat the
      // reader can reach across their projects, and is the only screen the
      // company-wide chat is reachable from.
      { path: 'chats', name: 'chats', component: () => import('@/views/chat/ChatsView.vue') },
      // Contributed by the Incidents plugin, and company-scoped rather than
      // project-scoped: an incident has no project until triage gives it one.
      { path: 'incidents', name: 'incidents', component: () => import('@/views/incidents/IncidentsView.vue') },
      // Contributed by the Voting plugin. Company-scoped like the chat list,
      // because a vote asks *people*, and a decision about the company's
      // direction belongs to no project at all.
      { path: 'votes', name: 'votes', component: () => import('@/views/voting/VotingView.vue') },
      { path: 'telegram/auth', name: 'telegram-auth', component: () => import('@/views/plugins/TelegramAuthView.vue') },
      { path: 'companies/new', name: 'company-new', component: () => import('@/views/company/CompanyCreateView.vue') },

      { path: 'projects/new', name: 'project-new', component: () => import('@/views/project/ProjectCreateView.vue') },
      {
        path: 'projects/:projectId',
        component: () => import('@/views/project/ProjectLayout.vue'),
        props: true,
        children: [
          { path: '', name: 'project', redirect: (to) => ({ name: 'project-boards', params: to.params }) },
          { path: 'boards', name: 'project-boards', component: () => import('@/views/project/BoardRedirect.vue') },
          { path: 'boards/manage', name: 'board-settings', component: () => import('@/views/board/BoardSettingsView.vue') },
          { path: 'archive', name: 'project-archive', component: () => import('@/views/board/ArchiveView.vue') },
          { path: 'boards/:boardId', name: 'board', component: () => import('@/views/board/BoardView.vue'), props: true },
          { path: 'cards/:cardId', name: 'card', component: () => import('@/views/card/CardView.vue'), props: true },
          { path: 'milestones', name: 'milestones', component: () => import('@/views/milestone/MilestonesView.vue') },
          { path: 'milestones/:milestoneId', name: 'milestone', component: () => import('@/views/milestone/MilestoneDetailView.vue'), props: true },
          { path: 'chats', name: 'project-chats', component: () => import('@/views/chat/ChatsView.vue') },
          // The same view as `/votes`, narrowed to one project by the presence
          // of `projectId` — a vote does not change shape when it belongs to a
          // project, only its audience does.
          { path: 'votes', name: 'project-votes', component: () => import('@/views/voting/VotingView.vue') },
          { path: 'wiki', name: 'wiki', component: () => import('@/views/wiki/WikiView.vue') },
          { path: 'wiki/:slug', name: 'wiki-page', component: () => import('@/views/wiki/WikiView.vue') },
          { path: 'workflow', name: 'workflow', component: () => import('@/views/workflow/WorkflowEditorView.vue') },
          { path: 'automation', name: 'automation', component: () => import('@/views/automation/AutomationView.vue') },
          // Contributed by the Flow Automation plugin; every route behind it is
          // gated server-side, and the view says so when the plugin is off.
          { path: 'flows', name: 'flows', component: () => import('@/views/flow/FlowAutomationView.vue') },
          { path: 'ai-chat', name: 'ai-chat', component: () => import('@/views/plugins/AiChatView.vue') },
          // Contributed by the Deploy Pipelines plugin; every route behind it is
          // gated server-side, and the view says so when the plugin is off.
          { path: 'deploy', name: 'deploy', component: () => import('@/views/deploy/DeployView.vue') },
          { path: 'webhooks', name: 'project-webhooks', component: () => import('@/views/settings/WebhooksView.vue') },
          { path: 'plugins', name: 'plugins', component: () => import('@/views/plugins/PluginsView.vue') },
          { path: 'plugins/:pluginKey/:page', name: 'plugin-page', component: () => import('@/views/plugins/PluginPageView.vue'), props: true },
          { path: 'settings', name: 'project-settings', component: () => import('@/views/project/ProjectSettingsView.vue') },
        ],
      },

      {
        path: 'company',
        component: () => import('@/views/company/CompanyLayout.vue'),
        children: [
          { path: '', name: 'company', redirect: { name: 'company-general' } },
          { path: 'general', name: 'company-general', component: () => import('@/views/company/CompanyGeneralView.vue') },
          { path: 'members', name: 'company-members', component: () => import('@/views/company/MembersView.vue') },
          {
            path: 'members/:userId',
            name: 'company-member',
            component: () => import('@/views/company/MemberProfileView.vue'),
            props: true,
          },
          { path: 'roles', name: 'company-roles', component: () => import('@/views/company/RolesView.vue') },
          { path: 'tags', name: 'company-tags', component: () => import('@/views/company/TagsView.vue') },
          // Company-only, including the project overrides: a variable belongs to
          // the company that owns the project, so one screen shows all of them
          // rather than an override being invisible from where it is inherited.
          { path: 'variables', name: 'company-variables', component: () => import('@/views/company/VariablesView.vue') },
          // The same two editors as the project screens, scoped by the absence
          // of a `projectId` param — see `useAutomationScope`. Company scope is
          // where events belonging to no project (incidents above all) are
          // automated, since a project rule can never see them.
          { path: 'automation', name: 'company-automation', component: () => import('@/views/automation/AutomationView.vue') },
          { path: 'flows', name: 'company-flows', component: () => import('@/views/flow/FlowAutomationView.vue') },
          // Company scope is where runners live: a machine is registered once
          // and lent to projects, so its screen cannot belong to any one of them.
          { path: 'deploy', name: 'company-deploy', component: () => import('@/views/deploy/DeployView.vue') },
          { path: 'plugins', name: 'company-plugins', component: () => import('@/views/company/CompanyPluginsView.vue') },
          { path: 'webhooks', name: 'company-webhooks', component: () => import('@/views/settings/WebhooksView.vue') },
          { path: 'localization', name: 'company-localization', component: () => import('@/views/company/LocalizationView.vue') },
          { path: 'sharing', name: 'company-sharing', component: () => import('@/views/company/SharingView.vue') },
          { path: 'audit', name: 'company-audit', component: () => import('@/views/company/AuditView.vue') },
        ],
      },

      {
        path: 'account',
        component: () => import('@/views/account/AccountLayout.vue'),
        children: [
          { path: '', name: 'account', redirect: { name: 'account-profile' } },
          { path: 'profile', name: 'account-profile', component: () => import('@/views/account/ProfileView.vue') },
          { path: 'notifications', name: 'account-notifications', component: () => import('@/views/account/NotificationPreferencesView.vue') },
          { path: 'appearance', name: 'account-appearance', component: () => import('@/views/account/AppearanceView.vue') },
          { path: 'shortcuts', name: 'account-shortcuts', component: () => import('@/views/account/ShortcutsView.vue') },
          { path: 'security', name: 'account-security', component: () => import('@/views/account/SecurityView.vue') },
          { path: 'api-tokens', name: 'account-api-tokens', component: () => import('@/views/account/ApiTokensView.vue') },
          /**
           * The restore list for archived companies and projects. It belongs to
           * the person rather than to a company on purpose: an archived company
           * is gone from every company-scoped screen, so the offer to bring it
           * back cannot live inside one.
           */
          { path: 'archive', name: 'account-archive', component: () => import('@/views/account/ArchiveView.vue') },
        ],
      },
    ],
  },
  /**
   * The instance control panel.
   *
   * A sibling of `/` rather than a child, because it does not belong in the
   * workspace shell. That shell puts you inside one company — its rail is a
   * company switcher over a project tree — while every screen below reads
   * across all of them, so `AdminShell` brings its own rail and its own chrome.
   *
   * `meta.superAdmin` keeps everybody else out; the guard below redirects, and
   * the `@SuperAdminOnly()` controller behind it is the check that matters.
   */
  {
    path: '/admin',
    component: () => import('@/views/admin/AdminShell.vue'),
    meta: { superAdmin: true },
    children: [
      { path: '', name: 'admin', redirect: { name: 'admin-overview' } },
      { path: 'overview', name: 'admin-overview', component: () => import('@/views/admin/AdminOverviewView.vue') },
      { path: 'users', name: 'admin-users', component: () => import('@/views/admin/AdminUsersView.vue') },
      { path: 'users/:userId', name: 'admin-user', component: () => import('@/views/admin/AdminUserDetailView.vue'), props: true },
      { path: 'companies', name: 'admin-companies', component: () => import('@/views/admin/AdminCompaniesView.vue') },
      {
        path: 'companies/:companyId',
        name: 'admin-company',
        component: () => import('@/views/admin/AdminCompanyDetailView.vue'),
        props: true,
      },
      { path: 'projects', name: 'admin-projects', component: () => import('@/views/admin/AdminProjectsView.vue') },
      { path: 'plugins', name: 'admin-plugins', component: () => import('@/views/admin/AdminPluginsView.vue') },
      { path: 'sessions', name: 'admin-sessions', component: () => import('@/views/admin/AdminSessionsView.vue') },
      { path: 'audit', name: 'admin-audit', component: () => import('@/views/admin/AdminAuditView.vue') },
      { path: 'maintenance', name: 'admin-maintenance', component: () => import('@/views/admin/AdminMaintenanceView.vue') },
    ],
  },
  /**
   * The external-sharing shell.
   *
   * A sibling of `/auth` and `/` rather than a child of either: a guest gets
   * neither the application chrome nor the sign-in card, and `meta.guest` keeps
   * the guard out of the way entirely — including the redirect that sends a
   * signed-in visitor to their dashboard, which would otherwise make a share
   * link unopenable by the very colleague testing it.
   *
   * The path is short on purpose. It is pasted into email and chat, and every
   * extra segment is a chance for a client to break the line in the middle.
   */
  {
    path: '/s/:token',
    name: 'share-guest',
    component: () => import('@/views/share/ShareGuestView.vue'),
    props: true,
    meta: { public: true, guest: true },
  },
  { path: '/:pathMatch(.*)*', name: 'not-found', component: () => import('@/views/NotFoundView.vue') },
];

/** Routes that render without a company scope, and must not be redirected away. */
const COMPANYLESS_ROUTES = new Set(['company-new', 'telegram-auth', 'account-archive']);

export const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior: (to, from, saved) => saved ?? { top: 0 },
});

router.beforeEach(async (to) => {
  // A guest route is served whoever is asking, and needs no session restored to
  // do it: the token in the URL is the only credential involved.
  if (to.matched.some((record) => record.meta.guest)) return true;

  const auth = useAuthStore();
  await auth.bootstrap();

  const isPublic = to.matched.some((record) => record.meta.public);

  if (!auth.isAuthenticated && !isPublic) {
    return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined };
  }
  if (auth.isAuthenticated && isPublic && to.name !== 'invitation' && to.name !== 'verify-email') {
    return { name: 'dashboard' };
  }

  // The panel has no company scope to resolve — it reads across all of them —
  // so it skips the workspace bootstrap below rather than exempting itself
  // from one branch of it.
  const isAdmin = to.matched.some((record) => record.meta.superAdmin);
  if (isAdmin && !auth.user?.isSuperAdmin) return { name: 'dashboard' };

  if (auth.isAuthenticated && !isAdmin) {
    try {
      const workspace = useWorkspaceStore();
      // Every authenticated screen needs a company scope for permissions.
      const company = workspace.company ?? (await workspace.ensureCompany());
      // `account-archive` belongs beside the two exemptions rather than behind
      // the redirect: somebody whose only company is archived has no company to
      // select, and the screen that would give it back is the one screen the
      // redirect would then make unreachable.
      if (!company && !COMPANYLESS_ROUTES.has(String(to.name))) return { name: 'company-new' };

      /**
       * A link may name the company it means.
       *
       * The `/company/...` screens carry no company in their path — they render
       * against whatever the workspace has selected — so a link to one of them
       * is ambiguous for anybody who belongs to more than one. A `?company=`
       * pins it, and honouring it here means every such link lands in the right
       * organisation whether it was clicked, pasted or reloaded.
       */
      const wantedCompany = typeof to.query.company === 'string' ? to.query.company : null;
      if (wantedCompany && workspace.companyId !== wantedCompany) {
        await workspace.selectCompany(wantedCompany);
      }

      const projectId = to.params.projectId as string | undefined;
      if (projectId && workspace.project?.id !== projectId) {
        await workspace.selectProject(projectId);
      }
    } catch {
      // Loading the workspace must never reject out of the guard: an aborted
      // navigation leaves the address bar on the new route with the previous
      // view still mounted, which reads as a permanently blank screen. If the
      // session died while these calls ran, the SDK has already cleared it —
      // send the user to the login page instead of nowhere.
      if (!auth.isAuthenticated) {
        return { name: 'login', query: to.fullPath !== '/' ? { redirect: to.fullPath } : undefined };
      }
    }
  }

  return true;
});
